/**
 * StepReview — Final wizard step: summary + collection binding + submit.
 *
 * Shows all wizard state for confirmation and allows
 * selecting/creating a collection before creating the datasource.
 */

import { useI18n } from '@/i18n';
import { Skeleton } from '@/components/ui';
import { Database, Folder, Settings, Link } from 'lucide-react';
import { useCollections } from '@/hooks/use-knowledge';
import { DatasourceProviderIcon } from '../ProviderIcons';
import type { EnrichedProviderManifest } from '../../types';
import type { BrowseSelection } from '../browse/types';

interface StepReviewProps {
  manifest: EnrichedProviderManifest;
  name: string;
  browseSelection: BrowseSelection | null;
  providerConfig: Record<string, unknown>;
  syncConfig: Record<string, unknown>;
  collectionId: string | null;
  onCollectionChange: (id: string | null) => void;
}

export function StepReview({
  manifest,
  name,
  browseSelection,
  providerConfig,
  syncConfig,
  collectionId,
  onCollectionChange,
}: StepReviewProps) {
  const { t } = useI18n();
  const { data: collectionsData, isLoading: collectionsLoading } = useCollections();

  // ISS-030: Only show collections eligible for binding.
  // The backend rejects binding when a collection already has a datasource
  // (source_type != null) or contains documents (document_count > 0).
  // Filter here so the user cannot select an ineligible collection.
  const allCollections = collectionsData?.data ?? [];
  const collections = allCollections.filter(
    (c) => !c.source_type && (c.document_count ?? 0) === 0,
  );
  const hiddenCount = allCollections.length - collections.length;

  return (
    <div className="max-w-lg mx-auto space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('datasources.wizard.review.title')}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          {t('datasources.wizard.review.subtitle')}
        </p>
      </div>

      {/* Provider */}
      <ReviewSection
        icon={<Database className="w-4 h-4" />}
        label={t('datasources.wizard.review.provider')}
      >
        <div className="flex items-center gap-2">
          <DatasourceProviderIcon providerType={manifest.provider_type} size={20} />
          <span>{manifest.display_name}</span>
        </div>
      </ReviewSection>

      {/* Name */}
      <ReviewSection
        icon={<Settings className="w-4 h-4" />}
        label={t('datasources.form.name')}
      >
        <span>{name || t('datasources.wizard.review.unnamed')}</span>
      </ReviewSection>

      {/* Browse selection */}
      {browseSelection && (
        <ReviewSection
          icon={<Folder className="w-4 h-4" />}
          label={t('datasources.wizard.review.selectedResource')}
        >
          <span>{browseSelection.label}</span>
        </ReviewSection>
      )}

      {/* Provider config summary */}
      {Object.keys(providerConfig).length > 0 && (
        <ReviewSection
          icon={<Settings className="w-4 h-4" />}
          label={t('datasources.wizard.review.configuration')}
        >
          <dl className="space-y-1">
            {Object.entries(providerConfig).map(([key, value]) => (
              <div key={key} className="flex justify-between text-sm">
                <dt className="text-gray-500 dark:text-gray-400">{key}</dt>
                <dd className="font-mono text-xs text-gray-900 dark:text-white truncate max-w-[200px]">
                  {String(value)}
                </dd>
              </div>
            ))}
          </dl>
        </ReviewSection>
      )}

      {/* Sync interval */}
      <ReviewSection
        icon={<Settings className="w-4 h-4" />}
        label={t('datasources.form.syncConfig')}
      >
        <span>
          {t('datasources.wizard.review.syncEvery', {
            minutes: String((syncConfig.interval_minutes as number) ?? 60),
          })}
        </span>
      </ReviewSection>

      {/* Collection binding */}
      <ReviewSection
        icon={<Link className="w-4 h-4" />}
        label={t('datasources.form.bindCollection')}
      >
        {collectionsLoading ? (
          <Skeleton className="h-8 w-full" />
        ) : (
          <select
            value={collectionId ?? ''}
            onChange={(e) => onCollectionChange(e.target.value || null)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t('datasources.form.noCollection')}</option>
            {collections.map((col) => (
              <option key={col.id} value={col.id}>
                {col.name}
              </option>
            ))}
          </select>
        )}
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {t('datasources.form.bindCollectionHint')}
        </p>
        {!collectionsLoading && hiddenCount > 0 && (
          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
            {t('datasources.form.bindCollectionHiddenHint', {
              count: String(hiddenCount),
            })}
          </p>
        )}
      </ReviewSection>
    </div>
  );
}

// =============================================================================
// ReviewSection
// =============================================================================

function ReviewSection({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <div className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">
        {icon}
        {label}
      </div>
      <div className="text-sm text-gray-900 dark:text-white">{children}</div>
    </div>
  );
}
